<template>
  <div class="c-pagination-area">
    <div class="c-pagination">
      <a href="#" class="first" @click.prevent="goToFirst">
        <i class="c-icon icon-double-arrow size-medium"></i>
        <span class="sr-only">처음페이지로 이동</span>
      </a>
      <a href="#" class="prev" @click.prevent="goToPrev">
        <i class="c-icon icon-arrow size-medium"></i>
        <span class="sr-only">이전</span>
      </a>
      <template v-for="pageNum in displayedPages" :key="pageNum">
        <strong v-if="pageNum === currentPage" class="is-active">{{
          pageNum
        }}</strong>
        <a v-else href="#" @click.prevent="goToPage(pageNum)">{{ pageNum }}</a>
      </template>
      <a href="#" class="next" @click.prevent="goToNext">
        <i class="c-icon icon-arrow size-medium"></i>
        <span class="sr-only">다음</span>
      </a>
      <a href="#" class="last" @click.prevent="goToLast">
        <i class="c-icon icon-double-arrow size-medium"></i>
        <span class="sr-only">끝페이지로 이동</span>
      </a>
    </div>

    <div v-if="showSelect" class="c-select type-small">
      <select v-model="itemsPerPage" @change="onItemsPerPageChange">
        <option v-for="count in perPageOptions" :key="count" :value="count">
          {{ count }}개씩 보기
        </option>
      </select>
    </div>
  </div>
</template>

<script setup name="pagination">
import {
  ref,
  computed,
  defineProps,
  defineEmits,
  defineExpose,
  watch,
} from "vue";

/***********************
 * PROPS
 **********************/

const props = defineProps({
  totalPages: {
    type: Number,
    required: true,
  },
  perPage: {
    type: Number,
    required: true,
  },
  showSelect: {
    type: Boolean,
    default: false,
  },
  pageReloadFlag: {
    type: Number,
    required: false,
  },
});

const emit = defineEmits(["page-change", "items-per-page-change"]);

/***********************
 * DATA
 **********************/

const currentPage = ref(1);
const itemsPerPage = ref(props.perPage); // old ref(20)
const perPageOptions = [5, 10, 20, 40, 60, 80, 100];

// 화면에 보여질 페이지 번호들 계산
const displayedPages = computed(() => {
  const pages = [];
  const maxPages = 10;
  let start = Math.max(1, currentPage.value - Math.floor(maxPages / 2));
  let end = Math.min(props.totalPages, start + maxPages - 1);

  if (end - start + 1 < maxPages) {
    start = Math.max(1, end - maxPages + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

/***********************
 * watch
 **********************/
watch(
  () => props.pageReloadFlag,
  (newValue) => {
    if (newValue && newValue > 0) {
      currentPage.value = 1;
      itemsPerPage.value = props.perPage;
    }
  }
);

/***********************
 * 함수
 **********************/

// 페이지 이동 메서드들
const goToPage = (page) => {
  currentPage.value = page;
  emit("page-change", page);
};

const goToFirst = () => {
  goToPage(1);
};

const goToLast = () => {
  goToPage(props.totalPages);
};

const goToPrev = () => {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1);
  }
};

const goToNext = () => {
  if (currentPage.value < props.totalPages) {
    goToPage(currentPage.value + 1);
  }
};

const onItemsPerPageChange = () => {
  emit("items-per-page-change", itemsPerPage.value);
};

const setCurrentPage = (page) => {
  currentPage.value = page;
};

defineExpose({
  goToPage,
  setCurrentPage,
});
</script>
